import { signal } from "@preact/signals";
import { useEffect, useRef, useState } from "preact/hooks";
import { toast } from "sonner";
import type { FileEntry, FileKind } from "~/protocol";
import { uploadFiles } from "~/web/src/api";
import { billingOn } from "~/web/src/billing-on";
import type { Channel } from "~/web/src/channel";
import { reportError } from "~/web/src/errors";
import { bytes } from "~/web/src/format";
import { setClipboard } from "../components/clipboard";
import { CopyIcon, ShareIcon, UnlockIcon } from "../components/icons";
import { unlockVideo } from "./top-pane";
import { CREDITS_TITLE, creditCount } from "./unlock-copy";

// The Files sheet: the flat listing (no subdirs, no dotfiles — the workspace
// never sends them). Click → the file shows in the top pane; ↓ downloads;
// ↑ upload → PUT (never overwrites), the watcher announces the entry; failures
// go to the error modal. A pending entry (still being generated) is dimmed.
// A video has Share (or Copy, once shared); a watermarked render also has
// Unlock, which re-renders it without the mark in place — the entry's
// `watermark` flips with the file's new version, and the button goes with it.
// The Sort by dropdown lives in the sheet head; its choice outlives the sheet.

export type FileSort = "name" | "mtime" | "kind";

export const fileSort = signal<FileSort>("name");

/** "File type" order: outputs first, then inputs. */
const KIND_ORDER: FileKind[] = ["video", "audio", "image", "text", "other"];

const byName = (a: FileEntry, b: FileEntry) => a.name.localeCompare(b.name);

const COMPARE: Record<FileSort, (a: FileEntry, b: FileEntry) => number> = {
  name: byName,
  // ISO strings order like the instants they name; newest first.
  mtime: (a, b) => b.mtime.localeCompare(a.mtime) || byName(a, b),
  kind: (a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind) || byName(a, b),
};

export function sorted(files: FileEntry[], sort: FileSort): FileEntry[] {
  return [...files].sort(COMPARE[sort]);
}

export function SortBy() {
  return (
    <label class="sort">
      Sort by:
      <select
        value={fileSort.value}
        onChange={(e) => {
          fileSort.value = (e.currentTarget as HTMLSelectElement).value as FileSort;
        }}
      >
        <option value="name">Name</option>
        <option value="mtime">Modified At</option>
        <option value="kind">File type</option>
      </select>
    </label>
  );
}

const GLYPH: Record<FileKind, string> = {
  text: "txt",
  image: "img",
  video: "mp4",
  audio: "mp3",
  other: "·",
};

export function Files({ channel, onPick }: { channel: Channel; onPick: (name: string) => void }) {
  const files = sorted(channel.files.value, fileSort.value);
  const input = useRef<HTMLInputElement>(null);
  const total = files.reduce((n, f) => n + f.size, 0);
  // Read again each time the sheet opens: another member may have spent since.
  useEffect(() => channel.refreshCredits(), [channel]);
  const credits = channel.credits.value;

  async function pick(e: Event) {
    const el = e.currentTarget as HTMLInputElement;
    const chosen = Array.from(el.files ?? []);
    el.value = "";
    await uploadFiles(channel.id, chosen, channel.org.value?.subscribed ?? false).catch(
      reportError,
    );
  }

  return (
    <>
      <div class="sb">
        <ul class="files">
          {files.map((f: FileEntry) => (
            <FileListItem key={f.name} file={f} channel={channel} onPick={onPick} />
          ))}
        </ul>
        {files.length === 0 && <div class="dim">No files yet.</div>}
        <div class="drop">
          Drop a logo, music track, or brand file anywhere to use it in the video
        </div>
      </div>
      <div class="sf">
        <span class="note">
          {files.length} files · {bytes(total)}
          {billingOn && credits !== null && (
            <span title={CREDITS_TITLE}> · {creditCount(credits)}</span>
          )}
        </span>
        <input ref={input} type="file" multiple hidden onChange={(e) => void pick(e)} />
        <button type="button" class="btn ghost" onClick={() => input.current?.click()}>
          ↑ Upload
        </button>
      </div>
    </>
  );
}

function FileListItem({
  channel,
  file,
  onPick,
}: {
  channel: Channel;
  file: FileEntry;
  onPick: (name: string) => void;
}) {
  // The share made from this row, valid for the version it was made for: an
  // overwrite (an unlock) is a new version with no share yet.
  const [shared, setShared] = useState<{ version: number; share: FileEntry["share"] } | null>(null);
  const f: FileEntry =
    shared && shared.version === file.version ? { ...file, share: shared.share } : file;
  const [unlocking, setUnlocking] = useState(false);
  return (
    <li key={f.name} class={f.pending ? "pending" : ""}>
      <button type="button" class="pick" onClick={() => onPick(f.name)}>
        <span class="ic">{f.pending ? "…" : GLYPH[f.kind]}</span>
        <span class="nm" title={f.name}>
          {f.name}
        </span>
        <span class="sz">{f.pending ? "generating" : bytes(f.size)}</span>
      </button>

      {f.kind === "video" &&
        (f.share?.link ? (
          <button
            type="button"
            class="icon ghost"
            onClick={async () => {
              await setClipboard(f.share?.link || "");
              toast.success("Copied!", {
                duration: 1500,
              });
            }}
          >
            <CopyIcon size={16} />
          </button>
        ) : (
          <button
            type="button"
            class="icon ghost"
            onClick={async () => {
              const newShareResult = await channel.send({
                kind: "share_project_file",
                fileName: f.name,
              });
              if (newShareResult.payload?.kind === "share_project_file") {
                setShared({
                  version: f.version,
                  share: {
                    id: newShareResult.payload.share.id,
                    link: newShareResult.payload.share.videoLink,
                  },
                });
              }
            }}
          >
            <ShareIcon size={16} />
          </button>
        ))}
      {billingOn && f.watermark === true && (
        <button
          type="button"
          class="icon ghost"
          title="Remove the watermark"
          // A fixed name: the content swaps to "…" while pending, and focus now stays here.
          aria-label="Remove the watermark"
          // Not `disabled`, which drops focus to the page (see top-pane.tsx's Unlock).
          aria-disabled={unlocking}
          onClick={async () => {
            if (unlocking) return;
            setUnlocking(true);
            await unlockVideo(channel, f.name);
            setUnlocking(false);
          }}
        >
          {unlocking ? "…" : <UnlockIcon size={16} />}
        </button>
      )}
      <a
        class="dl"
        aria-label={`Download ${f.name}`}
        href={channel.fileUrl(f.name, f.version)}
        download={f.name}
      >
        ↓
      </a>
    </li>
  );
}
